// LoginForm.tsx
import {
  BusinessCenter,
  Lock,
  Logout,
  Person,
  Security,
} from '@mui/icons-material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {
  createBusiness,
  getUser,
  logoutUser,
  startEnrolation,
  validateCredentials,
} from 'lib/api';
import { activateRelationBetweenReceiverAndReceiver } from 'lib/api/express';
import CustomAlert from 'lib/designSystem/Alert';
import EnhancedBox from 'lib/designSystem/BigBox';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { logout, updateUserEnrollment } from 'services/redux/authSlice'; // Import the action
import { RootState } from 'services/redux/store';

const AutoEnrollment = (props: any) => {
  const { addLoguot } = props;
  const domain = window.location.hostname;
  const clientId = domain.split('.')[0];
  const dispatch = useDispatch();
  const [termsAgree, setTermsAgree] = useState(false);
  const [canSeePassword, setCanSeePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    encryptedId: '',
    company: { taxIdentifier: '', name: '' },
  });
  const auth = useSelector((state: RootState) => state.auth);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openFailure, setOpenFailure] = useState(false);
  const [password, setPassword] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [enable, setEnable] = useState(false);

  const fetchUserProfile = async () => {
    setLoading(true);
    const userProfile = await getUser(dispatch, { id: auth.user?.id });
    setUserProfile(userProfile);
    setLoading(false);
  };

  const handleLogout = async () => {
    const response = await logoutUser(dispatch);
    if (response) {
      dispatch(logout());
    }
  };

  const handleEnable = async () => {
    try {
      await activateRelationBetweenReceiverAndReceiver(dispatch, clientId);
    } catch (error) {
      console.log(error);
    }
  };

  const handleEnrolation = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Validando credenciales');
      await validateCredentials(dispatch, {
        taxPassword: password,
        taxIdentifier: userProfile.company.taxIdentifier,
      });
      setLoadingMessage('Registrando empresa');
      const response = await createBusiness(dispatch, {
        name: userProfile.company.name,
        identifier: userProfile.company.taxIdentifier,
        taxPassword: password,
        enrolledPath: 'auto',
      });
      setLoadingMessage(
        'Comenzando sincronización con el SII, esto quedará en segundo plano. Por mientras, te redirigiremos a tu cuenta.',
      );
      await startEnrolation(dispatch, {
        taxPassword: password,
        taxIdentifier: userProfile.company.taxIdentifier,
        businessId: response.id,
      });
      dispatch(
        updateUserEnrollment({
          autoEnrrolled: true,
          businessId: response.id,
          businessIdentifier: userProfile.company.taxIdentifier,
        }),
      );
      if (enable) {
        await handleEnable();
      }
      setOpenSuccess(true);
    } catch (error: any) {
      console.log(error);
      setOpenFailure(true);
    }
    setLoading(false);
    setLoadingMessage('');
  };

  useEffect(() => {
    void fetchUserProfile();
  }, []);
  const handleClickShowPassword = () => {
    setCanSeePassword(!canSeePassword);
  };

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  if (loading)
    return <CenteredCircularProgress type="dialog" text={loadingMessage} />;

  return (
    <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh', px: 3, py: 2 }}>
      <Box sx={{ margin: 'auto', maxWidth: '600px' }}>
        <EnhancedBox>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <Person sx={{ fontSize: '1.5rem' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                ¡Bienvenido {userProfile.name.split(' ')[0]}!
              </Typography>
              <Chip
                label="Auto Enrolamiento"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '20px', fontSize: '0.7rem' }}
              />
            </Box>
            {addLoguot && (
              <Tooltip title="Cerrar sesión">
                <IconButton
                  onClick={() => void handleLogout()}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'error.main',
                      backgroundColor: 'rgba(211, 47, 47, 0.04)',
                    },
                  }}
                >
                  <Logout />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Divider sx={{ my: 1.5 }} />

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: '0.95rem',
              lineHeight: 1.5,
              mb: 3,
            }}
          >
            Comencemos conectando tu SII con Pruff Connect. Así podremos obtener
            la información que necesitas de manera eficiente y automatizada.
          </Typography>

          <Paper
            sx={{
              p: 2,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  borderRadius: '50%',
                  p: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BusinessCenter
                  sx={{ fontSize: '1rem', color: 'primary.main' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                >
                  RUT Asociado a tu Cuenta
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '1.1rem',
                  }}
                >
                  {userProfile.company.taxIdentifier}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Box sx={{ mb: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 2 }}
            >
              <Box
                sx={{
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '50%',
                  p: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Lock sx={{ fontSize: '1rem', color: 'success.main' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1.1rem',
                }}
              >
                Contraseña del SII
              </Typography>
            </Stack>
            <TextField
              fullWidth
              label="Contraseña"
              type={canSeePassword ? 'text' : 'password'}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="small"
                    >
                      {canSeePassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Paper
            sx={{
              p: 2,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 2 }}
            >
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  borderRadius: '50%',
                  p: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Security sx={{ fontSize: '1rem', color: 'warning.main' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1rem',
                }}
              >
                Permisos y Autorizaciones
              </Typography>
            </Stack>

            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => setEnable(!enable)}
                    value="allowExtraEmails"
                    color="primary"
                    checked={enable}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: '1.2rem' } }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.3 }}>
                    Autorizo a {clientId} a visualizar mis datos del SII y TGR
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => setTermsAgree(!termsAgree)}
                    value="allowExtraEmails"
                    color="primary"
                    checked={termsAgree}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: '1.2rem' } }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.3 }}>
                    He leído y estoy de acuerdo con los{' '}
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          'https://pruff-public.s3.amazonaws.com/documents/Terminos%2By%2BCondiciones.pdf',
                        );
                      }}
                      style={{
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontWeight: 500,
                      }}
                    >
                      términos y condiciones
                    </span>
                  </Typography>
                }
              />
            </Stack>
          </Paper>

          {/* Action Button */}
          <CustomButton
            onClick={() => handleEnrolation()}
            color="primary"
            disabled={!termsAgree || !password.length || !enable}
            fullWidth
          >
            Guardar
          </CustomButton>
        </EnhancedBox>
      </Box>
      <CustomAlert
        open={openSuccess}
        setOpen={setOpenSuccess}
        severity="success"
        text="Se están configurando tu cuenta, dentro de los próximos minutos ya podrás visualizar y operar tus facturas."
        handleClose={() => setOpenSuccess(false)}
      />
      <CustomAlert
        open={openFailure}
        setOpen={setOpenFailure}
        severity="error"
        text="La contraseña ingresada no es correcta o no es la del rut indicado, por favor intenta nuevamente."
        handleClose={() => setOpenFailure(false)}
      />
    </Box>
  );
};

export default AutoEnrollment;
